import { FC, useMemo } from 'react';

import { FlexColumn, FlexRow } from '@editor/components/utilities/layouts';
import { withEventScope } from '@repo/event-processor';
import { useTranslation } from '@repo/i18n';
import type { SubscriptionPlanType } from '@repo/subscription/api';
import BellIcon from '@repo/ui/components/Icon/BellIcon';
import StarIcon from '@repo/ui/components/Icon/StarIcon';
import LockOpenIcon from '@repo/ui/components/Icon/v2/LockOpenIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { H3, H5, P2 } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';
import format from 'date-fns/format';

import {
  FREE_TRIAL_DAYS_AMOUNT,
  TRIAL_EXPIRE_NOTIFICATION_DAYS,
} from '../../global/constants';

import { NewFreeTrialIconWrapper, TrialStepsContainer } from './styles';

type TrialExplanationProps = {
  startDate: Date;
  plan: SubscriptionPlanType;
};

const TrialExplanation: FC<TrialExplanationProps> = ({ startDate, plan }) => {
  const { t } = useTranslation();

  const trialSteps = useMemo(
    () => [
      {
        title: t('subscriptionModal:trial_explanation.title_today'),
        description: t(
          'subscriptionModal:trial_explanation.description_today',
          {
            context: plan.toLocaleLowerCase(),
          }
        ),
        Icon: (
          <LockOpenIcon
            height="20px"
            theme={{ color: themeStyle.varBackgroundAlt }}
          />
        ),
      },
      {
        title: t('subscriptionModal:trial_explanation.title_mid', {
          number: FREE_TRIAL_DAYS_AMOUNT - TRIAL_EXPIRE_NOTIFICATION_DAYS,
        }),
        description: t('subscriptionModal:trial_explanation.description_mid'),
        Icon: (
          <BellIcon height="20px" theme={{ color: themeStyle.varInkMedium }} />
        ),
      },
      {
        title: t('subscriptionModal:trial_explanation.title_end', {
          number: FREE_TRIAL_DAYS_AMOUNT,
        }),
        description: t('subscriptionModal:trial_explanation.description_end', {
          date: format(startDate, 'MMM dd'),
        }),
        Icon: (
          <StarIcon height="20px" theme={{ color: themeStyle.varInkMedium }} />
        ),
      },
    ],
    [t, plan, startDate]
  );

  return (
    <FlexColumn gap="36px">
      <H3>{t('subscriptionModal:trial_explanation.heading')}</H3>
      <TrialStepsContainer gap="48px">
        {trialSteps.map((item) => (
          <FlexRow key={item.title} alignItems="center">
            <NewFreeTrialIconWrapper>{item.Icon}</NewFreeTrialIconWrapper>
            <Spacer width="16px" />
            <FlexColumn>
              <H5>{item.title}</H5>
              <P2>{item.description}</P2>
            </FlexColumn>
          </FlexRow>
        ))}
      </TrialStepsContainer>
    </FlexColumn>
  );
};

export default withEventScope(TrialExplanation, { module: 'trialExplanation' });
